<div class="flex flex-wrap mt-8 mb-6">

    <div class="w-full flex flex-row justify-between mb-4">
        <app-btn-back routeTo=".." btnText="Cancel" icon="fa fa-close"></app-btn-back>
    </div>

    <div class="mb-2 border-0 w-full">
        <h1 class="font-semibold text-2xl text-primary-500">
            New Academic Year Configurator
        </h1>
        <p class="text-sm text-primary-300 mt-1">
            Nothing changes for teachers, students or parents until you click Finish. Your choices are saved as you go,
            so you can leave and continue later.
        </p>
    </div>

    <div class="cursor-default flex flex-col my-4 items-center space-y-4 w-full">

        @for (title of steps; track $index) {
        @let step = $index + 1;
        <div class="flex flex-col w-full">
            <div
                class="flex flex-row space-x-2 items-center flex-nowrap p-4 {{ activeStep == step ? 'bg-primary-500' : 'bg-primary-100' }}">
                <div
                    class="flex items-center justify-center w-6 h-6 {{ activeStep == step ? 'bg-white text-primary' : 'bg-primary-200 text-white' }} rounded-full">
                    {{ step }}
                </div>
                <span class="text-sm font-medium {{ activeStep == step ? 'text-white' : 'text-primary-300' }}">{{ title
                    }}</span>
                @if (saving && activeStep == step) {
                <span class="text-xs text-white ml-auto">Saving...</span>
                }
            </div>

            @if (activeStep == step) {
            <div class="border-l-2 border-primary-100 ml-6 my-4 w-full">
                <div class="mx-4 p-4">
                    @switch (step) {

                    <!-- Step 1: academic year -->
                    @case (1) {
                    @if (loading && !draft) {
                    <div class="animate-pulse h-4 w-1/2 bg-slate-200 rounded my-4"></div>
                    } @else if (error) {
                    <p class="text-sm text-red-500 mb-4">{{ error }}</p>
                    } @else if (isDone) {
                    <p class="text-sm text-primary-500 mb-2">The academic year has already been switched to {{ toYear
                        }}.</p>
                    } @else if (isFinalizing) {
                    <p class="text-sm text-primary-500 mb-2">Another leader is finishing this New Academic Year. Check
                        back in a few minutes.</p>
                    } @else {
                    <p class="text-sm text-primary-300 mb-4">Current academic year
                        <span class="font-semibold text-primary-500">{{ fromYear }}</span>
                        &rarr; new academic year <span class="font-semibold text-primary-500">{{ toYear }}</span>
                    </p>
                    @if (draft) {
                    <p class="text-xs text-primary-300 mb-4">A draft for {{ toYear }} is already in progress; you'll
                        continue where it was left.</p>
                    }
                    <app-btn-action [label]="draft ? 'Continue' : 'Start'" (clicked)="onStart()" icon=""
                        [inProgress]="loading" bgColor="bg-primary-500 hover:bg-primary-400"
                        suffixIcon="fa fa-chevron-right"></app-btn-action>
                    }
                    }

                    <!-- Step 2: graduating students -->
                    @case (2) {
                    <p class="mb-2 text-primary-300 text-sm">Mark students who are graduating or leaving {{ group?.name }}
                        as Alumni. Everyone else continues into {{ toYear }}.</p>
                    <p class="mb-4 text-primary-300 text-xs">Alumni keep their membership, so they can still open
                        this school, see their certificates and share their history.</p>
                    <div class="flex flex-row w-full">
                        <div class="w-1/6 flex flex-col">
                            @for (grade of currGrades; track grade.id) {
                            <div (click)="selectGrade(grade.id)"
                                class="{{ selectedGradeId === grade.id ? 'bg-white' : 'bg-blueGray-100' }} hover:bg-white p-4 w-full cursor-pointer">
                                <p class="text-xs text-primary-300">{{ fromYear }}</p>
                                <p class="text-sm">{{ grade.gradeName }}</p>
                                <p class="text-xs text-primary-300">{{ studentsOf(grade.id).length }} students</p>
                            </div>
                            }
                            @if (ungradedStudents.length > 0) {
                            <div (click)="selectGrade(undefined)"
                                class="{{ selectedGradeId === undefined ? 'bg-white' : 'bg-blueGray-100' }} hover:bg-white p-4 w-full cursor-pointer">
                                <p class="text-sm">Not in a grade</p>
                                <p class="text-xs text-primary-300">{{ ungradedStudents.length }} students</p>
                            </div>
                            }
                        </div>
                        <div class="w-5/6 p-4 bg-white">
                            <table class="items-center w-full bg-transparent text-sm border-collapse table-auto">
                                <thead>
                                    <tr>
                                        <th
                                            class="px-6 py-3 uppercase font-semibold text-left bg-blueGray-50 text-blueGray-500">
                                            Name</th>
                                        <th
                                            class="px-6 py-3 uppercase font-semibold text-left bg-blueGray-50 text-blueGray-500">
                                            Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (student of studentsOf(selectedGradeId); track student.memberId) {
                                    <tr>
                                        <td class="px-6 py-2 whitespace-nowrap">
                                            <div class="flex items-center">
                                                @if (student.photoURL) {
                                                <img [src]="student.photoURL"
                                                    class="h-10 w-10 bg-white object-cover rounded-full" />
                                                } @else {
                                                <div
                                                    class="bg-primary-100 uppercase text-primary-500 flex justify-center items-center h-10 w-10 rounded-full">
                                                    {{ student.name | initials }}
                                                </div>
                                                }
                                                <span class="ml-3 font-bold text-blueGray-600">{{ student.name }}</span>
                                            </div>
                                        </td>
                                        <td class="px-6 py-2">
                                            <select (change)="onChangeStatus($event, student)" [disabled]="saving"
                                                class="border-0 px-3 py-2 bg-white rounded text-sm shadow focus:ring-0 w-full">
                                                <option [selected]="student.status !== statuses.alumni"
                                                    [value]="statuses.active">Continuing</option>
                                                <option [selected]="student.status === statuses.alumni"
                                                    [value]="statuses.alumni">Alumni</option>
                                            </select>
                                        </td>
                                    </tr>
                                    } @empty {
                                    <tr>
                                        <td class="px-6 py-4 text-primary-300" colspan="2">No active students</td>
                                    </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="flex flex-row mt-4 gap-2">
                        <app-btn-action label="Next" (clicked)="setActiveStep(3)" icon=""
                            bgColor="bg-primary-500 hover:bg-primary-400" suffixIcon="fa fa-chevron-right"
                            [inProgress]="saving"></app-btn-action>
                    </div>
                    }

                    <!-- Step 3: new grades -->
                    @case (3) {
                    <p class="mb-4 text-primary-300 text-sm">Choose the {{ fromYear }} grades that continue into {{ toYear
                        }}, and add any new ones. Grades you don't keep are archived.</p>
                    <div class="flex flex-row w-full gap-4 flex-nowrap">
                        <div class="w-1/2 flex flex-col gap-2">
                            <h2 class="text-lg font-bold">{{ fromYear }} Grades</h2>
                            <div class="bg-white px-4 py-2 rounded min-h-48">
                                @for (grade of currGrades; track grade.id) {
                                <label class="py-2 flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" [checked]="isKept(grade.id!)" [disabled]="saving"
                                        (change)="onToggleKeep(grade)" class="rounded ring-0 focus:ring-0" />
                                    <span>{{ grade.gradeName }}</span>
                                    <span class="text-xs text-primary-300 ml-auto">{{ isKept(grade.id!) ? 'Keep' :
                                        'Archive' }}</span>
                                </label>
                                } @empty {
                                <p class="py-2 text-sm text-primary-300">No active grades in {{ fromYear }}</p>
                                }
                            </div>
                        </div>
                        <div class="w-1/2 flex flex-col gap-2">
                            <div class="flex flex-row w-full items-center justify-between">
                                <h2 class="text-lg font-bold">{{ toYear }} Grades</h2>
                                <button (click)="onAddGrade()" type="button" [disabled]="saving"
                                    class="bg-transparent text-secondary-500 text-sm px-2">Add Grade</button>
                            </div>
                            <div class="bg-white px-4 py-2 rounded min-h-48">
                                @for (grade of newGrades; track grade.id) {
                                <div class="flex flex-row justify-between items-center py-2">
                                    <div class="flex items-center">
                                        <i class="fa {{ grade.added ? 'fa-add' : 'fa-check' }} mr-2"></i>
                                        <p>{{ grade.gradeName }}</p>
                                        @if (grade.added) {
                                        <p class="text-xs text-primary-400 ml-2 px-2 rounded-lg bg-gray-100">new</p>
                                        }
                                    </div>
                                    @if (grade.added) {
                                    <button (click)="onRemoveAddedGrade(grade.id)" type="button" [disabled]="saving">
                                        <i class="fa fa-trash-can text-red-500 px-4 py-2"></i>
                                    </button>
                                    }
                                </div>
                                } @empty {
                                <p class="py-2 text-sm text-primary-300">Keep or add at least one grade</p>
                                }
                            </div>
                        </div>
                    </div>
                    <div class="flex flex-row mt-4 gap-2">
                        <app-btn-action label="Back" (clicked)="setActiveStep(2)"
                            bgColor="bg-primary-500 hover:bg-primary-400" icon="fa fa-chevron-left"></app-btn-action>
                        <app-btn-action label="Next" (clicked)="setActiveStep(4)" icon=""
                            bgColor="bg-primary-500 hover:bg-primary-400" suffixIcon="fa fa-chevron-right"
                            [inProgress]="saving"></app-btn-action>
                    </div>
                    }

                    <!-- Step 4: teachers -->
                    @case (4) {
                    <p class="mb-4 text-primary-300 text-sm">Tag one teacher to each {{ toYear }} grade. A teacher can
                        take only one grade.</p>
                    <div class="flex flex-row flex-nowrap">
                        <h2 class="w-1/2 text-lg font-bold">{{ toYear }} Grades</h2>
                        <h2 class="w-1/2 text-lg font-bold">Teacher</h2>
                    </div>
                    @for (grade of newGrades; track grade.id) {
                    <div class="flex flex-row w-full bg-white mb-2 p-2">
                        <div class="w-1/2 flex items-center">
                            <p>{{ grade.gradeName }}</p>
                        </div>
                        <div class="w-1/2">
                            <select (change)="onChangeTeacher($event, grade.id)" [disabled]="saving"
                                class="border-0 px-3 py-2 bg-white rounded text-sm shadow focus:ring-0 w-full">
                                <option value="" [selected]="teacherOf(grade.id) === ''">Unassigned</option>
                                @for (teacher of teachers; track teacher.memberId) {
                                <option [value]="teacher.memberId"
                                    [selected]="teacherOf(grade.id) === teacher.memberId"
                                    [disabled]="isTeacherTaken(teacher.memberId, grade.id)">
                                    {{ teacher.name }}{{ isTeacherTaken(teacher.memberId, grade.id) ? ' (tagged)' : ''
                                    }}</option>
                                }
                            </select>
                        </div>
                    </div>
                    } @empty {
                    <p class="text-sm text-primary-300">Add grades in the previous step first.</p>
                    }
                    <div class="flex flex-row mt-4 gap-2">
                        <app-btn-action label="Back" (clicked)="setActiveStep(3)"
                            bgColor="bg-primary-500 hover:bg-primary-400" icon="fa fa-chevron-left"></app-btn-action>
                        <app-btn-action label="Next" (clicked)="onTeachersNext()" icon=""
                            bgColor="bg-primary-500 hover:bg-primary-400" suffixIcon="fa fa-chevron-right"
                            [inProgress]="saving"></app-btn-action>
                    </div>
                    }

                    <!-- Step 5: place students -->
                    @case (5) {
                    <p class="mb-2 text-primary-300 text-sm">Choose each student's {{ toYear }} grade. Students in a
                        kept grade stay in it unless you change it.</p>
                    <p class="mb-4 text-primary-300 text-xs">Select several students to change them together. "Remove"
                        takes the student out of the school; "Skip" leaves them untouched and doesn't archive them.</p>
                    <div class="flex flex-row w-full">
                        <div class="w-1/6 flex flex-col">
                            @for (grade of currGrades; track grade.id) {
                            <div (click)="selectGrade(grade.id)"
                                class="{{ selectedGradeId === grade.id ? 'bg-white' : 'bg-blueGray-100' }} hover:bg-white p-4 w-full cursor-pointer">
                                <p class="text-xs text-primary-300">{{ fromYear }}</p>
                                <p class="text-sm">{{ grade.gradeName }}</p>
                            </div>
                            }
                            @if (ungradedStudents.length > 0) {
                            <div (click)="selectGrade(undefined)"
                                class="{{ selectedGradeId === undefined ? 'bg-white' : 'bg-blueGray-100' }} hover:bg-white p-4 w-full cursor-pointer">
                                <p class="text-sm">Not in a grade</p>
                            </div>
                            }
                        </div>
                        <div class="w-5/6 p-4 bg-white">
                            <table class="items-center w-full bg-transparent text-sm border-collapse table-auto">
                                <thead>
                                    <tr>
                                        <th class="px-2 py-3 bg-blueGray-50 w-8">
                                            <input type="checkbox" (change)="onToggleAllStudents($event)"
                                                [checked]="selectedStudentIds.size > 0 && selectedStudentIds.size === continuingOf(selectedGradeId).length"
                                                class="rounded ring-0 focus:ring-0" />
                                        </th>
                                        <th
                                            class="px-6 py-3 uppercase font-semibold text-left bg-blueGray-50 text-blueGray-500">
                                            Name</th>
                                        <th
                                            class="px-6 py-3 uppercase font-semibold text-left bg-blueGray-50 text-blueGray-500">
                                            {{ toYear }} Grade</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (student of continuingOf(selectedGradeId); track student.memberId) {
                                    @let placement = placementOf(student);
                                    <tr class="{{ selectedStudentIds.has(student.memberId) ? 'bg-blueGray-100' : '' }}">
                                        <td class="px-2 py-2">
                                            <input type="checkbox" (change)="onToggleStudent(student)"
                                                [checked]="selectedStudentIds.has(student.memberId)"
                                                class="rounded ring-0 focus:ring-0" />
                                        </td>
                                        <td class="px-6 py-2 whitespace-nowrap font-bold text-blueGray-600">
                                            {{ student.name }}
                                            @if (placement === placeUnassigned) {
                                            <span
                                                class="ml-2 text-xs font-normal text-red-500 px-2 rounded-lg bg-red-50">needs
                                                a grade</span>
                                            }
                                        </td>
                                        <td class="px-6 py-2">
                                            <select (change)="onChangePlacement($event, student)" [disabled]="saving"
                                                class="border-0 px-3 py-2 bg-white rounded text-sm shadow focus:ring-0 w-full">
                                                <option [value]="placeUnassigned"
                                                    [selected]="placement === placeUnassigned">Unassigned</option>
                                                @for (grade of newGrades; track grade.id) {
                                                <option [value]="grade.id" [selected]="placement === grade.id">
                                                    {{ grade.gradeName }}</option>
                                                }
                                                <option [value]="placeRemove" [selected]="placement === placeRemove">
                                                    Remove from school</option>
                                                <option [value]="placeSkip" [selected]="placement === placeSkip">
                                                    Skip (don't archive)</option>
                                            </select>
                                        </td>
                                    </tr>
                                    } @empty {
                                    <tr>
                                        <td class="px-6 py-4 text-primary-300" colspan="3">No continuing students</td>
                                    </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="flex flex-row mt-4 gap-2">
                        <app-btn-action label="Back" (clicked)="setActiveStep(4)"
                            bgColor="bg-primary-500 hover:bg-primary-400" icon="fa fa-chevron-left"></app-btn-action>
                        <app-btn-action label="Next" (clicked)="setActiveStep(6)" icon=""
                            bgColor="bg-primary-500 hover:bg-primary-400" suffixIcon="fa fa-chevron-right"
                            [inProgress]="saving"></app-btn-action>
                    </div>
                    }

                    <!-- Step 6: review and finish -->
                    @case (6) {
                    @if (isDone) {
                    <p class="mb-2 text-primary-500 text-sm">The academic year has been switched to {{ toYear }}.</p>
                    @if (result) {
                    <p class="mb-4 text-primary-300 text-sm">{{ result.promoted ?? 0 }} students continue,
                        {{ result.alumni ?? 0 }} alumni, {{ result.removed ?? 0 }} removed, {{ result.skipped ?? 0 }}
                        skipped. {{ result.archivedStudents ?? 0 }} student records archived.</p>
                    }
                    <app-btn-back routeTo=".." btnText="Return" bgColors="bg-secondary-500 hover:bg-secondary-600"
                        textColors="text-white" icon=""></app-btn-back>
                    } @else {
                    <p class="mb-4 text-primary-300 text-sm">Finish archives {{ fromYear }}, moves everyone into
                        {{ toYear }}, and switches the school's academic year. This can't be undone; individual records
                        can be corrected afterwards.</p>
                    <ul class="mb-4 text-sm text-primary-500 list-disc ml-6">
                        <li>{{ newGrades.length }} grades in {{ toYear }}</li>
                        <li>{{ summary.promoted }} students continue</li>
                        <li>{{ summary.alumni }} alumni</li>
                        <li>{{ summary.removed }} removed from the school</li>
                        <li>{{ summary.skipped }} skipped (not archived)</li>
                    </ul>
                    @if (problems.length > 0) {
                    <div class="mb-4 p-3 rounded bg-red-50 text-sm text-red-600">
                        @for (problem of problems; track $index) {
                        <p>{{ problem }}</p>
                        }
                    </div>
                    }
                    <div class="flex flex-row gap-2">
                        <app-btn-action label="Back" (clicked)="setActiveStep(5)"
                            bgColor="bg-primary-500 hover:bg-primary-400" icon="fa fa-chevron-left"></app-btn-action>
                        <app-btn-action label="Finish" (clicked)="onFinish()" icon=""
                            bgColor="bg-secondary-500 hover:bg-secondary-400" [inProgress]="finishing"
                            [isDisabled]="problems.length > 0 || saving"></app-btn-action>
                    </div>
                    }
                    }
                    }
                </div>
            </div>
            }
        </div>
        }
    </div>
</div>
